import {
  createSlice,
  type Draft,
  type PayloadAction,
  type SliceCaseReducers,
  type ValidateSliceCaseReducers,
} from "@reduxjs/toolkit"

// Factory-owned fields (never setPreferences inputs).
export interface PanelStateBase {
  isPreferencesLoaded: boolean
  pendingClonePersist?: boolean
}

// dashboardId → panelId → state.
export type PanelMap<S> = Partial<Record<string, S>>
export type PanelRootState<S> = Partial<Record<string, PanelMap<S>>>

// Whitelist-eligible keys: excludes the factory-owned base fields.
type PreferenceField<S> = Exclude<keyof S, keyof PanelStateBase>

// Helpers passed to a slice's bespoke reducers (operate on the Immer draft in place).
export interface PanelSliceHelpers<S extends PanelStateBase> {
  ensure: (state: Draft<PanelRootState<S>>, dashboardId: string, panelId: string) => S
  initInstance: (
    state: Draft<PanelRootState<S>>,
    dashboardId: string,
    panelId: string,
    overrides?: Partial<S>,
  ) => void
}

export interface CreatePanelSliceConfig<
  S extends PanelStateBase,
  K extends PreferenceField<S>,
  CR extends SliceCaseReducers<PanelRootState<S>>,
> {
  name: string
  defaultState: S
  // setPreferences whitelist; the inferred key union also types its payload.
  preferenceFields: readonly K[]
  // Deep-clone mutable containers on a fresh instance (e.g. graph disabledItems, kpi sectionSettings).
  freshOverrides?: (defaultState: S) => Partial<S>
  // Re-clone containers + reset session fields on clone; factory adds pendingClonePersist: true.
  cloneOverrides?: (sourceState: S) => Partial<S>
  // The slice's bespoke setX (+ optional initFromModuleConfig) reducers.
  reducers: (helpers: PanelSliceHelpers<S>) => ValidateSliceCaseReducers<PanelRootState<S>, CR>
}

// Builds a panel-scoped slice (dashboardId → panelId → state) with the shared scaffolding:
// ensureInstance, whitelisted setPreferences, clonePanelState/clearPendingClonePersist/removePanel.
export function createPanelSlice<
  S extends PanelStateBase,
  K extends PreferenceField<S>,
  CR extends SliceCaseReducers<PanelRootState<S>>,
>(config: CreatePanelSliceConfig<S, K, CR>) {
  const { name, defaultState, preferenceFields, freshOverrides, cloneOverrides, reducers } = config

  const freshInstance = (): S => ({ ...defaultState, ...freshOverrides?.(defaultState) })

  const ensure: PanelSliceHelpers<S>["ensure"] = (draft, dashboardId, panelId) => {
    // Draft<S> won't simplify for a generic S; operate on the plain type (same object at runtime).
    const state = draft as unknown as PanelRootState<S>
    if (!state[dashboardId]) state[dashboardId] = {}
    const panels = state[dashboardId] as PanelMap<S>
    if (!panels[panelId]) panels[panelId] = freshInstance()
    return panels[panelId] as S
  }

  const initInstance: PanelSliceHelpers<S>["initInstance"] = (draft, dashboardId, panelId, overrides) => {
    const state = draft as unknown as PanelRootState<S>
    if (state[dashboardId]?.[panelId] != null) return
    if (!state[dashboardId]) state[dashboardId] = {}
    const panels = state[dashboardId] as PanelMap<S>
    panels[panelId] = { ...freshInstance(), ...overrides }
  }

  return createSlice({
    name,
    initialState: {} as PanelRootState<S>,
    reducers: {
      setPreferences(
        state,
        action: PayloadAction<{ dashboardId: string, panelId: string } & Partial<Pick<S, K>>>,
      ) {
        const { dashboardId, panelId } = action.payload
        const instance = ensure(state, dashboardId, panelId)
        const prefs = action.payload as Partial<Pick<S, K>>
        for (const field of preferenceFields) {
          const value = prefs[field]
          if (value !== undefined) instance[field] = value
        }
        instance.isPreferencesLoaded = true
      },

      clonePanelState(
        state,
        action: PayloadAction<{ dashboardId: string, sourcePanelId: string, newPanelId: string }>,
      ) {
        const { dashboardId, sourcePanelId, newPanelId } = action.payload
        const root = state as unknown as PanelRootState<S>
        const sourceState = root[dashboardId]?.[sourcePanelId]
        if (!sourceState) return
        if (!root[dashboardId]) root[dashboardId] = {}
        const panels = root[dashboardId] as PanelMap<S>
        panels[newPanelId] = {
          ...sourceState,
          ...cloneOverrides?.(sourceState),
          pendingClonePersist: true,
        }
      },

      clearPendingClonePersist(
        state,
        action: PayloadAction<{ dashboardId: string, panelId: string }>,
      ) {
        const { dashboardId, panelId } = action.payload
        const root = state as unknown as PanelRootState<S>
        const instance = root[dashboardId]?.[panelId]
        if (instance) delete instance.pendingClonePersist
      },

      removePanel(
        state,
        action: PayloadAction<{ dashboardId: string, panelId: string }>,
      ) {
        const { dashboardId, panelId } = action.payload
        const root = state as unknown as PanelRootState<S>
        const panels = root[dashboardId]
        if (!panels) return
        root[dashboardId] = Object.fromEntries(
          Object.entries(panels).filter(([key]) => key !== panelId),
        )
      },

      // Drops the whole dashboardId key on delete, clearing the empty {} that per-panel removePanel
      // leaves. Filtered rebuild avoids dynamic-delete lint.
      removeInstance(
        state,
        action: PayloadAction<{ dashboardId: string }>,
      ) {
        const { dashboardId } = action.payload
        const root = state as unknown as PanelRootState<S>
        if (root[dashboardId] === undefined) return
        const next = Object.fromEntries(
          Object.entries(root).filter(([key]) => key !== dashboardId),
        )
        return next as unknown as typeof state
      },

      ...reducers({ ensure, initInstance }),
    },
  })
}
